/**
 * Shared WebAuthn registration-ceremony helpers — the base64url<->ArrayBuffer
 * codecs and the `navigator.credentials.create(...)` orchestration extracted
 * from `pages/WebAuthnCredentials.tsx` (the self-service passkey page) so the
 * first-run setup wizard's EnrollMFA step (Phase E) can run the IDENTICAL
 * browser ceremony against the setup-token-gated webauthn/register/begin+
 * complete endpoints instead of re-implementing the ArrayBuffer plumbing.
 *
 * go-webauthn (the server library) speaks base64url (no padding) on the
 * wire, but the browser's WebAuthn API requires ArrayBuffer challenge/user-id/
 * credential-id fields — every helper here exists solely to bridge that gap.
 */

/** Decode a base64url string to an ArrayBuffer (WebAuthn API requires ArrayBuffer inputs). */
export function b64urlToBuffer(s: string): ArrayBuffer {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/')
  const bin = atob(b64)
  const arr = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i)
  return arr.buffer
}

/** Encode an ArrayBuffer to base64url (no padding) — go-webauthn's expected wire format. */
export function bufToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let bin = ''
  bytes.forEach((byte) => {
    bin += String.fromCharCode(byte)
  })
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

export interface RegisterBeginOptions {
  publicKey: {
    challenge: string
    rp: { id?: string; name: string }
    user: { id: string; name: string; displayName: string }
    pubKeyCredParams: Array<{ type: string; alg: number }>
    timeout?: number
    excludeCredentials?: Array<{
      id: string
      type: string
      transports?: string[]
    }>
    authenticatorSelection?: Record<string, unknown>
    attestation?: string
  }
}

/**
 * The wire-shaped payload `.../register/complete` expects — mirrors
 * go-webauthn's ParseCredentialCreationResponse input. Carries an index
 * signature so it satisfies `Record<string, unknown>` structurally (the
 * generated client's `credential` field is typed that way), without
 * widening the named fields callers actually rely on.
 */
export interface RegisterCompletePayload {
  [key: string]: unknown
  id: string
  rawId: string
  type: string
  response: {
    clientDataJSON: string
    attestationObject: string
  }
}

/**
 * Converts the server's `.../register/begin` options (base64url strings, as
 * received over JSON) into the ArrayBuffer-shaped `CredentialCreationOptions`
 * the browser's `navigator.credentials.create` requires, invokes the
 * ceremony, and returns BOTH the raw `PublicKeyCredential` (for callers that
 * need `.id`/other metadata) and the base64url-encoded payload ready to POST
 * to `.../register/complete`.
 */
export async function runRegistrationCeremony(
  opts: RegisterBeginOptions
): Promise<{
  credential: PublicKeyCredential
  payload: RegisterCompletePayload
}> {
  const { publicKey: pk } = opts

  const excludeCredentials = (pk.excludeCredentials ?? []).map((c) => ({
    id: b64urlToBuffer(c.id),
    type: c.type as PublicKeyCredentialType,
    transports: c.transports as AuthenticatorTransport[] | undefined,
  }))

  const credential = (await navigator.credentials.create({
    publicKey: {
      challenge: b64urlToBuffer(pk.challenge),
      rp: pk.rp,
      user: {
        id: b64urlToBuffer(pk.user.id),
        name: pk.user.name,
        displayName: pk.user.displayName,
      },
      pubKeyCredParams: pk.pubKeyCredParams as PublicKeyCredentialParameters[],
      timeout: pk.timeout,
      excludeCredentials,
      authenticatorSelection: pk.authenticatorSelection as
        | AuthenticatorSelectionCriteria
        | undefined,
      attestation: pk.attestation as
        | AttestationConveyancePreference
        | undefined,
    },
  })) as PublicKeyCredential
  const response = credential.response as AuthenticatorAttestationResponse

  const payload: RegisterCompletePayload = {
    id: credential.id,
    rawId: bufToB64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufToB64url(response.clientDataJSON),
      attestationObject: bufToB64url(response.attestationObject),
    },
  }

  return { credential, payload }
}

export interface AuthenticateBeginOptions {
  publicKey: {
    challenge: string
    timeout?: number
    rpId?: string
    allowCredentials?: Array<{ id: string; type: string; transports?: string[] }>
    userVerification?: string
  }
}

/**
 * The wire-shaped assertion payload `.../authenticate/complete` expects —
 * mirrors go-webauthn's ParseCredentialRequestResponseBody input. Same
 * index-signature rationale as RegisterCompletePayload.
 */
export interface AuthenticateCompletePayload {
  [key: string]: unknown
  id: string
  rawId: string
  type: string
  response: {
    authenticatorData: string
    clientDataJSON: string
    signature: string
    userHandle: string | null
  }
}

/**
 * Converts `.../authenticate/begin` options (base64url strings, as received
 * over JSON) into the ArrayBuffer-shaped `CredentialRequestOptions` the
 * browser's `navigator.credentials.get` requires, invokes the assertion
 * ceremony, and returns the base64url-encoded payload ready to POST to
 * `.../authenticate/complete`. Mirrors runRegistrationCeremony's role for
 * the login/step-up ceremony instead of registration.
 */
export async function runAuthenticationCeremony(
  opts: AuthenticateBeginOptions
): Promise<AuthenticateCompletePayload> {
  const { publicKey: pk } = opts

  const allowCredentials = (pk.allowCredentials ?? []).map((c) => ({
    id: b64urlToBuffer(c.id),
    type: c.type as PublicKeyCredentialType,
    transports: c.transports as AuthenticatorTransport[] | undefined,
  }))

  const credential = (await navigator.credentials.get({
    publicKey: {
      challenge: b64urlToBuffer(pk.challenge),
      timeout: pk.timeout,
      rpId: pk.rpId,
      allowCredentials,
      userVerification: pk.userVerification as UserVerificationRequirement | undefined,
    },
  })) as PublicKeyCredential
  const response = credential.response as AuthenticatorAssertionResponse

  return {
    id: credential.id,
    rawId: bufToB64url(credential.rawId),
    type: credential.type,
    response: {
      authenticatorData: bufToB64url(response.authenticatorData),
      clientDataJSON: bufToB64url(response.clientDataJSON),
      signature: bufToB64url(response.signature),
      userHandle: response.userHandle ? bufToB64url(response.userHandle) : null,
    },
  }
}
